import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { IlluminateIcon, ExecuteIcon, KnowledgeIcon, AskIcon } from './TabBarIcons';
import { COLORS } from '../lib/theme';
import { t } from '../i18n/locale';
import { useLocaleStore } from '../stores/locale-store';
import styles from './DesktopLayout.module.css';

const tabs = [
  { path: '/illuminate', labelKey: 'nav.illuminate', en: 'Illuminate', Icon: IlluminateIcon },
  { path: '/ask', labelKey: 'nav.ask', en: 'Ask', Icon: AskIcon },
  { path: '/knowledge', labelKey: 'nav.knowledge', en: 'Knowledge', Icon: KnowledgeIcon },
  { path: '/execute', labelKey: 'nav.execute', en: 'Execute', Icon: ExecuteIcon },
] as const;

export function DesktopLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = useLocaleStore((s) => s.locale);

  return (
    <div className={styles.layout}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <p className={styles.brandName}>AETERNA</p>
          <p className={styles.brandSub}>观复知常</p>
        </div>

        <nav className={styles.nav} role="tablist" aria-label={t(locale, 'nav.main')}>
          {tabs.map((tab) => {
            const isActive = location.pathname === tab.path || location.pathname.startsWith(`${tab.path}/`);
            const label = t(locale, tab.labelKey);
            return (
              <button
                key={tab.path}
                role="tab"
                aria-selected={isActive}
                aria-label={label}
                onClick={() => navigate(tab.path)}
                className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
              >
                <div className={styles.navIcon}>
                  <tab.Icon
                    size={18}
                    color={isActive ? COLORS.clay : COLORS.ash}
                    strokeWidth={isActive ? 1.8 : 1.5}
                  />
                </div>
                <span className={`${styles.navLabel} ${isActive ? styles.navLabelActive : ''}`}>
                  {label}
                </span>
                <span className={styles.navEnLabel}>{tab.en}</span>
              </button>
            );
          })}
        </nav>

        <div className={styles.sidebarFooter}>
          <button
            className={styles.profileButton}
            onClick={() => navigate('/profile')}
          >
            <div className={styles.profileAvatar}>
              <User size={14} strokeWidth={1.5} />
            </div>
            <span className={styles.profileName}>{t(locale, 'nav.profile')}</span>
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className={styles.main}>
        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
